import type { Stroke } from '../../types';
import type { SvgExportConfig } from '../../utils/export';
import { ExportManagerBase } from '../../utils/ExportManagerBase';
import { COLORS, PADDING } from '../../config/constants';
import { renderDiagramSvgContent } from '../diagram/rendering/DiagramSvgRenderer';
import { getDiagramBounds } from '../diagram/state/diagramBounds';
import { buildStrokeSvgPath, getStrokeOutlinePoints } from '../draw/strokeUtils';
import type { UnifiedStore } from './UnifiedStore';
import { getCanvasContext } from '../../utils/canvas';

interface UnifiedExportContext {
  store: UnifiedStore;
  getCanvas: () => HTMLCanvasElement;
  isInverted: () => boolean;
  drawStrokeToLayer: (stroke: Stroke, ctx: CanvasRenderingContext2D) => void;
  clearSelection: () => void;
  selectById: (id: string) => void;
  render: () => void;
}

type UnifiedExportOptions = { transparent?: boolean; scale?: number; selectionOnly?: boolean };

export class UnifiedExportManager extends ExportManagerBase<UnifiedExportOptions> {
  protected readonly prefix = 'drawling';
  private readonly context: UnifiedExportContext;

  constructor(context: UnifiedExportContext) {
    super();
    this.context = context;
  }

  protected isInverted(): boolean {
    return this.context.isInverted();
  }

  protected getSvgExportConfig(): SvgExportConfig {
    const canvas = this.context.getCanvas();
    return {
      padding: PADDING.export,
      darkColor: COLORS.ink,
      fallbackSize: { width: canvas.width, height: canvas.height },
      viewBoxOnEmpty: true,
      backgroundMode: 'percent',
    };
  }

  protected getSvgBounds(): ReturnType<typeof this.getContentBounds> {
    return this.getContentBounds();
  }

  protected buildSvgContent(
    translate: { x: number; y: number },
    options: { transparent: boolean }
  ): string {
    const store = this.context.store;
    let svg = '';

    // Strokes
    for (const stroke of store.strokes) {
      if (stroke.points.length === 0) continue;
      const isEraser = stroke.tool === 'eraser';
      if (isEraser && options.transparent) continue;

      const strokeColor = isEraser
        ? (this.isInverted() ? '#000000' : '#ffffff')
        : stroke.color;

      const outlinePoints = getStrokeOutlinePoints(stroke);
      const pathData = buildStrokeSvgPath(outlinePoints, translate);
      if (!pathData) continue;
      svg += `  <path d="${pathData}" fill="${strokeColor}"/>\n`;
    }

    // Diagram elements + text
    svg += renderDiagramSvgContent({
      translate,
      shapes: store.shapes,
      connectors: store.connectors,
      textElements: store.textElements,
      measureCtx: this.getMeasureContext(),
      inverted: this.isInverted(),
    });

    return svg;
  }

  protected getDataUrl(options: UnifiedExportOptions = {}): string {
    return this.getImageData(options);
  }

  getImageData(options: UnifiedExportOptions = {}): string {
    const transparent = options.transparent ?? false;
    const scale = options.scale ?? 1;
    const selectionOnly = options.selectionOnly ?? false;
    const store = this.context.store;

    const selectedIds = [...store.selectedElements];
    this.context.clearSelection();

    const selectedIdSet = new Set(selectedIds);
    const hasSelection = selectionOnly && selectedIds.length > 0;

    const shapesToExport = hasSelection
      ? store.shapes.filter(s => selectedIdSet.has(s.id))
      : store.shapes;
    const connectorsToExport = hasSelection
      ? store.connectors.filter(c => selectedIdSet.has(c.id))
      : store.connectors;
    const textElementsToExport = hasSelection
      ? store.textElements.filter(t => selectedIdSet.has(t.id))
      : store.textElements;
    const strokesToExport = hasSelection
      ? store.strokes.filter(s => selectedIdSet.has(s.id))
      : store.strokes;

    const bounds = this.computeExportBounds(shapesToExport, connectorsToExport, textElementsToExport, strokesToExport);
    const padding = PADDING.export;

    const renderAll = ({
      output,
      createLayer,
    }: {
      output: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D };
      createLayer: () => { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null;
    }): void => {
      // Draw shapes
      for (const drawShape of store.drawShapes) {
        if (hasSelection && !selectedIdSet.has(drawShape.id)) continue;
        drawShape.render(output.ctx);
      }

      // Strokes (offscreen compositing for erasers)
      const strokeLayer = createLayer();
      if (strokeLayer) {
        for (const stroke of strokesToExport) {
          this.context.drawStrokeToLayer(stroke, strokeLayer.ctx);
        }
        output.ctx.drawImage(strokeLayer.canvas, 0, 0);
      }

      // Diagram elements
      for (const connector of connectorsToExport) {
        connector.render(output.ctx, this.isInverted());
      }
      for (const shape of shapesToExport) {
        shape.render(output.ctx);
      }
      for (const textElement of textElementsToExport) {
        textElement.render(output.ctx);
      }
    };

    const data = this.renderRasterDataUrl({
      bounds,
      padding,
      scale,
      transparent,
      darkColor: COLORS.ink,
      render: renderAll,
    });

    // Restore selection
    for (const id of selectedIds) {
      this.context.selectById(id);
    }
    this.context.render();

    return data;
  }

  getContentBounds(): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const store = this.context.store;
    return this.computeExportBounds(store.shapes, store.connectors, store.textElements, store.strokes);
  }

  private computeExportBounds(
    shapes: typeof UnifiedStore.prototype.shapes,
    connectors: typeof UnifiedStore.prototype.connectors,
    textElements: typeof UnifiedStore.prototype.textElements,
    strokes: Stroke[]
  ): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const measureCtx = this.getMeasureContext();

    const diagramBounds = getDiagramBounds({ shapes, connectors, textElements, measureCtx });

    let minX = diagramBounds?.minX ?? Infinity;
    let minY = diagramBounds?.minY ?? Infinity;
    let maxX = diagramBounds?.maxX ?? -Infinity;
    let maxY = diagramBounds?.maxY ?? -Infinity;

    for (const stroke of strokes) {
      for (const p of stroke.points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
    }

    if (minX === Infinity) return null;
    return { minX, minY, maxX, maxY };
  }

  private getMeasureContext(): CanvasRenderingContext2D {
    return getCanvasContext(document.createElement('canvas'));
  }
}
